import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import experiences from '../data/experiences';
import education from '../data/education';
import skills from '../data/skills';

const SEO_CONFIG = {
  home: {
    en: {
      title: 'Samir Djelal – Official Website',
      description: 'Official website of Samir Djelal — Software Engineer, Problem Solver, and Full Stack Developer specializing in web technologies, embedded systems, and information security.',
    },
    ar: {
      title: 'سمير جلال – الموقع الرسمي',
      description: 'الموقع الرسمي لسمير جلال — مهندس برمجيات ومطوّر Full Stack متخصص في تقنيات الويب، برمجة الأنظمة، وأمن المعلومات.',
    },
    pageType: 'ProfilePage',
  },
  experience: {
    en: {
      title: 'Experience | Samir Djelal',
      description: 'Explore the professional experience of Samir Djelal as a Software Engineer and Full Stack Developer.',
    },
    ar: {
      title: 'الخبرة | سمير جلال',
      description: 'تعرّف على الخبرات المهنية لسمير جلال كمهندس برمجيات ومطوّر Full Stack.',
    },
    pageType: 'ProfilePage',
  },
  skills: {
    en: {
      title: 'Skills | Samir Djelal',
      description: 'Discover the technical skills and expertise of Samir Djelal.',
    },
    ar: {
      title: 'المهارات | سمير جلال',
      description: 'اكتشف المهارات والخبرات التقنية لسمير جلال.',
    },
    pageType: 'ProfilePage',
  },
  education: {
    en: {
      title: 'Education | Samir Djelal',
      description: 'Learn about the educational background and qualifications of Samir Djelal as a Software Engineer.',
    },
    ar: {
      title: 'التعليم | سمير جلال',
      description: 'تعرّف على الخلفية التعليمية والمؤهلات الأكاديمية لسمير جلال.',
    },
    pageType: 'ProfilePage',
  },
  contact: {
    en: {
      title: 'Contact | Samir Djelal',
      description: 'Get in touch with Samir Djelal to discuss projects or professional opportunities.',
    },
    ar: {
      title: 'اتصل بي | سمير جلال',
      description: 'تواصل مع سمير جلال لمناقشة المشاريع أو الفرص المهنية.',
    },
    pageType: 'ContactPage',
  },
  services: {
    en: {
      title: 'Services & Platforms | Samir Djelal',
      description: 'Explore engineering platforms and technical advisory services offered by Samir Djelal, including Trax and fintech gateways.',
    },
    ar: {
      title: 'الخدمات والمنصات | سمير جلال',
      description: 'استكشف المنصات الهندسية وخدمات الاستشارة التقنية المقدمة من سمير جلال، بما في ذلك Trax وبوابات الدفع.',
    },
    pageType: 'WebPage',
  },
  cv: {
    en: {
      title: 'Executive CV | Samir Djelal – Group CTO & Solutions Officer',
      description: 'Executive Curriculum Vitae of Samir Djelal — Group Chief Technology and Solutions Officer, Systems Architect specializing in Rust, Laravel, and PCI DSS v4.0.1.',
    },
    ar: {
      title: 'السيرة الذاتية التنفيذية | سمير جلال – رئيس قطاع التكنولوجيا والحلول للمجموعة',
      description: 'السيرة الذاتية الرسمية التنفيذية لسمير جلال — رئيس قطاع التكنولوجيا والحلول للمجموعة، خبير الأنظمة الموزعة، Rust، Laravel، والامتثال التنظيمي.',
    },
    pageType: 'ProfilePage',
  },
};

function SEO() {
  const location = useLocation();
  const { lang } = useLanguage();

  const baseUrl = 'https://samirdjelal.com';

  // Normalize path and compute language variants
  const { pathWithoutLang, enUrl, arUrl, canonicalUrl } = useMemo(() => {
    const rawPath = location.pathname || '/';
    const parts = rawPath.split('/').filter(Boolean);
    const isAr = parts[0] === 'ar';
    const stripped = isAr ? `/${parts.slice(1).join('/')}` : `/${parts.join('/')}`;
    const normalized = stripped.replace(/\/+/g, '/');
    const finalPath = normalized === '/' ? '/' : normalized;

    const en = `${baseUrl}${finalPath === '/' ? '' : finalPath}` || baseUrl;
    const ar = `${baseUrl}/ar${finalPath === '/' ? '' : finalPath}`;

    return {
      pathWithoutLang: finalPath,
      enUrl: en || baseUrl,
      arUrl: ar,
      canonicalUrl: (lang === 'ar') ? ar : en || baseUrl,
    };
  }, [location.pathname, lang]);

  // Compute title, description, and page type based on route and language
  const { pageTitle, pageDescription, pageType } = useMemo(() => {
    const p = pathWithoutLang;
    const routeKey = (p === '/' || p === '')
      ? 'home'
      : ['experience', 'skills', 'education', 'contact', 'services', 'cv'].find(key => p.startsWith(`/${key}`)) || 'home';

    const config = SEO_CONFIG[routeKey] || SEO_CONFIG.home;
    const localized = config[lang] || config.en;

    return {
      pageTitle: localized.title,
      pageDescription: localized.description,
      pageType: config.pageType || 'WebPage',
    };
  }, [pathWithoutLang, lang]);

  // Keywords relevant to multilingual targeting
  const keywords = lang === 'ar'
    ? 'سمير, جلال, سمير جلال, جلال سمير, مهندس برمجيات, مطور, تطوير ويب, أمن المعلومات, نظم مدمجة, ذكاء اصطناعي, وكلاء الذكاء الاصطناعي, أتمتة, n8n, MCP, اتصل بـ سمير جلال, تواصل'
    : 'Samir Djelal, djelal, djelal samir, samir, Software Engineer, Full Stack Developer, Web Development, AI Agents, AI Automation, n8n, MCP Server, Problem Solver, Embedded Systems, Information Security, Contact Samir Djelal, Hire Software Engineer';

  // Generate breadcrumb structured data
  const breadcrumbData = useMemo(() => {
    const items = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://samirdjelal.com"
      }
    ];

    if (location.pathname !== '/') {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const pageName = pathSegments[0].charAt(0).toUpperCase() + pathSegments[0].slice(1);

      items.push({
        "@type": "ListItem",
        "position": 2,
        "name": pageName,
        "item": `https://samirdjelal.com${location.pathname}`
      });
    }

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items
    };
  }, [location.pathname]);

  // Generate experience structured data
  const experienceData = useMemo(() => {
    if (location.pathname !== '/experience') return null;

    const workExperience = experiences.map(exp => ({
      "@type": "WorkPosition",
      "name": exp.title,
      "worksFor": {
        "@type": "Organization",
        "name": exp.company
      },
      "description": exp.description,
      "skills": exp.skills ? exp.skills.join(', ') : '',
      "startDate": exp.period.split(' to ')[0],
      "endDate": exp.period.includes('to') ? exp.period.split(' to ')[1].split(' · ')[0] : 'Present'
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": workExperience.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": item
      })),
      "numberOfItems": workExperience.length,
      "name": "Samir Djelal's Work Experience"
    };
  }, [location.pathname, experiences]);

  // Generate education structured data
  const educationData = useMemo(() => {
    if (location.pathname !== '/education') return null;

    const educationalExperience = education.map(edu => ({
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": edu.degree,
      "educationalLevel": "Degree",
      "recognizedBy": {
        "@type": "Organization",
        "name": edu.institution
      },
      "description": edu.description || `${edu.degree} from ${edu.institution}`,
      "competencyRequired": edu.skills ? edu.skills.join(', ') : ''
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": educationalExperience.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": item
      })),
      "numberOfItems": educationalExperience.length,
      "name": "Samir Djelal's Educational Background"
    };
  }, [location.pathname, education]);

  // Generate skills structured data
  const skillsData = useMemo(() => {
    if (location.pathname !== '/skills') return null;

    const skillsList = Object.entries(skills).reduce((acc, [category, skillItems]) => {
      skillItems.forEach(skill => {
        acc.push({
          "@type": "DefinedTerm",
          "name": skill.name,
          "inDefinedTermSet": category
        });
      });
      return acc;
    }, []);

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": skillsList.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": item
      })),
      "numberOfItems": skillsList.length,
      "name": "Samir Djelal's Skills and Expertise"
    };
  }, [location.pathname, skills]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Samir Djelal" />

      {/* Canonical & hreflang (language-specific) */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" href={enUrl} hrefLang="en" />
      <link rel="alternate" href={arUrl} hrefLang="ar" />
      <link rel="alternate" href={enUrl} hrefLang="x-default" />

      {/* Open Graph Meta Tags for social sharing */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === 'ar' ? 'ar' : 'en'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://samirdjelal.com/profile.png" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@samirdjelal" />
      <meta name="twitter:creator" content="@samirdjelal" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://samirdjelal.com/profile.png" />

      {/* Structured Data (JSON-LD) for Person */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Samir Djelal",
            "alternateName": ["جلال سمير", "سمير جلال", "Samir Djelal", "Djelal Samir", "samir djelal", "Djelal", "Samir"],
            "url": "https://samirdjelal.com",
            "image": "https://samirdjelal.com/profile.png",
            "jobTitle": "Software Engineer",
            "description": "Software Engineer, Problem Solver, and Full Stack Developer specialized in AI Agents and Automation",
            "sameAs": [
              "https://twitter.com/samirdjelal",
              "https://github.com/samirdjelal",
              "https://instagram.com/samirdjelal",
              "https://www.linkedin.com/in/samirdjelal"
            ],
            "knowsAbout": [
              "AI Agents & Autonomous Systems",
              "MCP Server Development",
              "Workflow Automation",
              "Web Technologies",
              "Cross-platform Desktop Apps",
              "Mobile App Development",
              "Systems Programming",
              "Embedded Systems",
              "IoT",
              "Networking",
              "Information Security"
            ],
            "speaksLanguage": ["en", "ar"],
            "skills": [
              "AI Agent Development",
              "Prompt Engineering",
              "n8n Automation",
              "MCP",
              "Rust",
              "PHP",
              "JavaScript",
              "TypeScript",
              "Go",
              "UI/UX Design",
              "Performance Optimization",
              "Security"
            ]
          }
        `}
      </script>

      {/* Structured Data (JSON-LD) for WebSite */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Samir Djelal - Software Engineer",
            "url": "https://samirdjelal.com",
            "description": "Personal website of Samir Djelal, Software Engineer and Full Stack Developer",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://samirdjelal.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        `}
      </script>

      {/* Structured Data (JSON-LD) for current page */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "${pageType}",
            "url": "${canonicalUrl}",
            "inLanguage": "${lang}",
            "name": "${pageTitle}",
            "description": "${pageDescription}",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Samir Djelal - Software Engineer",
              "url": "https://samirdjelal.com"
            },
            "about": {
              "@type": "Person",
              "name": "Samir Djelal"
            },
            "mainEntity": {
              "@type": "Person",
              "name": "Samir Djelal"
            }
          }
        `}
      </script>

      {/* Structured Data (JSON-LD) for BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>

      {/* Structured Data (JSON-LD) for Experience page */}
      {location.pathname === '/experience' && (
        <script type="application/ld+json">
          {JSON.stringify(experienceData)}
        </script>
      )}

      {/* Structured Data (JSON-LD) for Education page */}
      {location.pathname === '/education' && (
        <script type="application/ld+json">
          {JSON.stringify(educationData)}
        </script>
      )}

      {/* Structured Data (JSON-LD) for Skills page */}
      {location.pathname === '/skills' && (
        <script type="application/ld+json">
          {JSON.stringify(skillsData)}
        </script>
      )}

      {/* Structured Data (JSON-LD) for ProfessionalService */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Samir Djelal - Software Engineering Services",
            "url": "https://samirdjelal.com",
            "image": "https://samirdjelal.com/profile.png",
            "description": "Professional software engineering services including web development, mobile app development, and system programming.",
            "address": {
              "@type": "PostalAddress"
            },
            "priceRange": "$$",
            "serviceType": [
              "Web Development",
              "Mobile App Development",
              "Desktop App Development",
              "System Programming",
              "System Architecture",
              "Cloud Infrastructure",
              "DevOps",
              "UI/UX Design"
            ],
            "provider": {
              "@type": "Person",
              "name": "Samir Djelal"
            }
          }
        `}
      </script>
    </Helmet>
  );
}

export default SEO;
